import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Copy, Download, ExternalLink, Star } from "lucide-react";

function QRCard({ title, desc, url, fileName, testid, highlight = false }) {
  const printRef = useRef(null);

  const download = () => {
    const canvas = printRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${fileName}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("Print-quality QR code downloaded.");
  };

  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied.");
  };

  return (
    <div data-testid={testid} className={`bg-[#0C0C0C] border rounded-xl p-6 ${highlight ? "border-[#F2B705]/50" : "border-white/10"}`}>
      <h3 className="font-display font-bold text-lg flex items-center gap-2">
        {highlight && <Star size={16} className="text-[#F2B705]" />} {title}
      </h3>
      <p className="text-white/50 text-sm mt-1">{desc}</p>
      <div className="mt-5 bg-white p-4 rounded-xl w-fit mx-auto">
        <QRCodeCanvas value={url} size={180} level="M" includeMargin={false} />
      </div>
      {/* hidden high-res canvas used for print-quality downloads */}
      <div ref={printRef} className="hidden" aria-hidden="true">
        <QRCodeCanvas value={url} size={1200} level="Q" includeMargin={true} />
      </div>
      <div className="mt-4 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-xs text-[#F2B705] break-all">{url}</div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid={`${testid}-download`} onClick={download} className="flex items-center gap-2 bg-[#F2B705] text-black text-sm font-bold px-4 py-2 rounded-md hover:bg-white transition-colors">
          <Download size={15} /> Download QR (print size)
        </button>
        <button data-testid={`${testid}-copy`} onClick={copy} className="flex items-center gap-2 border border-white/15 text-sm px-4 py-2 rounded-md hover:bg-white hover:text-black transition-colors">
          <Copy size={15} /> Copy link
        </button>
        <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-white/15 text-sm px-4 py-2 rounded-md hover:bg-white hover:text-black transition-colors">
          <ExternalLink size={15} /> Open
        </a>
      </div>
    </div>
  );
}

export default function SharePanel() {
  const origin = window.location.origin;
  return (
    <div data-testid="admin-share-panel">
      <p className="text-white/50 text-sm mb-5">Share these links or print the QR codes (shirts, flyers, car windows, business cards). Every submission flows straight into your CRM.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl">
        <QRCard
          title="Weekly Special Page"
          desc="Your $375/week offer with the lead form. Uses the short link — tagged weekly-special in GoHighLevel, and QR scans are tracked separately in the Weekly Special report."
          url={`${origin}/special?src=qr`}
          fileName="autohire-weekly-special-qr"
          testid="share-weekly-special"
          highlight
        />
        <QRCard
          title="Drivers Club — Glove Box Card"
          desc="🚗 Love your rental? Join the AutoHire Drivers Club — earn points, get rewards, refer friends. Print one for every glove box."
          url={`${origin}/club?src=qr`}
          fileName="autohire-drivers-club-qr"
          testid="share-drivers-club"
          highlight
        />
        <QRCard
          title="Contact Us Page"
          desc="Send this to capture a lead's name, email & phone — synced to GoHighLevel automatically."
          url={`${origin}/contact`}
          fileName="autohire-contact-qr"
          testid="share-contact"
        />
        <QRCard
          title="Book a Car"
          desc="Sends people straight into the booking flow to reserve a vehicle."
          url={`${origin}/?book=1`}
          fileName="autohire-booknow-qr"
          testid="share-booknow"
        />
        <QRCard
          title="Leave a Review"
          desc="Send to customers after their rental. Reviews need your approval before showing on the site."
          url={`${origin}/review`}
          fileName="autohire-review-qr"
          testid="share-review"
        />
      </div>
    </div>
  );
}
